import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { cloneDeep } from 'lodash';

import type {
  FerryIngredient,
  FlightIngredient,
  HotelIngredient,
  RecipeMainIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import {
  isFlightIngredient,
  isOwnTransportIngredient,
  isStayIngredient,
} from '@apps/main/modules/recipe/ingredients/typeGuards';

export const useEditingIngredientStore = defineStore(
  'editingIngredientStore',
  () => {
    // State
    const editingStayIngredient = ref<
      HotelIngredient | OwnStayIngredient | null
    >(null);
    const editingFlightIngredient = ref<FlightIngredient | null>(null);
    const editingFlightIngredientStartingStatus = ref<FlightIngredient | null>(
      null
    );
    const editingFerryIngredient = ref<FerryIngredient | null>(null);
    const editingFerryIngredientStartingStatus = ref<FerryIngredient | null>(
      null
    );
    const flightIngredientFrom = ref<'recipe' | 'itinerary' | null>(null);
    const ferryIngredientFrom = ref<'recipe' | 'itinerary' | null>(null);

    // Getters
    const editingTransportIngredient = computed<
      FlightIngredient | FerryIngredient | null
    >(() =>
      editingFlightIngredient.value
        ? editingFlightIngredient.value
        : editingFerryIngredient.value
    );

    // Actions
    function setEditingIngredient(
      ingredient: RecipeMainIngredient,
      from: 'recipe' | 'itinerary'
    ): void {
      if (isOwnTransportIngredient(ingredient)) {
        return;
      }
      if (isStayIngredient(ingredient)) {
        setStayEditingIngredient(ingredient);
      } else {
        setTransportEditingIngredient(ingredient, from);
      }
    }

    function setStayEditingIngredient(
      ingredient: HotelIngredient | OwnStayIngredient
    ): void {
      editingStayIngredient.value = ingredient;
    }

    function setFlightEditingIngredient(
      ingredient: FlightIngredient,
      from: 'recipe' | 'itinerary'
    ): void {
      flightIngredientFrom.value = from;
      saveFlightIngredientStartingStatus(ingredient);
      editingFlightIngredient.value = ingredient;
    }

    function setFerryEditingIngredient(
      ingredient: FerryIngredient,
      from: 'recipe' | 'itinerary'
    ): void {
      ferryIngredientFrom.value = from;
      saveFerryIngredientStartingStatus(ingredient);
      editingFerryIngredient.value = ingredient;
    }

    function setTransportEditingIngredient(
      ingredient: FerryIngredient | FlightIngredient,
      from: 'recipe' | 'itinerary'
    ): void {
      isFlightIngredient(ingredient)
        ? setFlightEditingIngredient(ingredient, from)
        : setFerryEditingIngredient(ingredient, from);
    }

    function saveFlightIngredientStartingStatus(
      ingredient?: FlightIngredient
    ): void {
      editingFlightIngredientStartingStatus.value = ingredient
        ? cloneDeep(ingredient)
        : null;
    }

    function saveFerryIngredientStartingStatus(
      ingredient?: FerryIngredient
    ): void {
      editingFerryIngredientStartingStatus.value = ingredient
        ? cloneDeep(ingredient)
        : null;
    }

    async function doneEditingIngredient(): Promise<void> {
      if (editingStayIngredient.value) {
        editingStayIngredient.value.polygonConstraint = undefined;
      }
      editingStayIngredient.value = null;
      editingFlightIngredient.value = null;
      editingFerryIngredient.value = null;
      editingFlightIngredientStartingStatus.value = null;
      editingFerryIngredientStartingStatus.value = null;
    }

    return {
      // State
      editingStayIngredient,
      editingFlightIngredient,
      editingFlightIngredientStartingStatus,
      editingFerryIngredient,
      editingFerryIngredientStartingStatus,
      flightIngredientFrom,
      ferryIngredientFrom,

      // Getters
      editingTransportIngredient,

      // Actions
      setEditingIngredient,
      setStayEditingIngredient,
      setFlightEditingIngredient,
      setFerryEditingIngredient,
      setTransportEditingIngredient,
      saveFlightIngredientStartingStatus,
      saveFerryIngredientStartingStatus,
      doneEditingIngredient,
    };
  }
);
